import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/client";

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: api.getMe,
    retry: false,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: api.getHealth,
    retry: false,
    staleTime: Infinity,
  });
}

export function useRides() {
  return useQuery({
    queryKey: ["rides"],
    queryFn: api.getRides,
  });
}

export function useRide(id: string | undefined) {
  return useQuery({
    queryKey: ["rides", id],
    queryFn: () => api.getRide(id!),
    enabled: !!id,
  });
}

export function useRidePoints(id: string | undefined) {
  return useQuery({
    queryKey: ["rides", id, "points"],
    queryFn: () => api.getRidePoints(id!),
    enabled: !!id,
    staleTime: Infinity,
  });
}

export function useSummary() {
  return useQuery({
    queryKey: ["summary"],
    queryFn: api.getSummary,
  });
}

export function useUploadRides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.uploadRides,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rides"] });
      void qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

export function useRenameRide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.renameRide(id, title),
    onSuccess: (ride) => {
      void qc.invalidateQueries({ queryKey: ["rides"] });
      qc.setQueryData(["rides", ride.id], ride);
    },
  });
}

export function useDeleteRide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteRide,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rides"] });
      void qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

export function useApiTokens() {
  return useQuery({
    queryKey: ["apiTokens"],
    queryFn: api.getApiTokens,
  });
}

export function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createApiToken,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apiTokens"] });
    },
  });
}

export function useDeleteApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteApiToken,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["apiTokens"] });
    },
  });
}
